import { useCheckboxField } from '@modusign/react-signing';
import { MouseEvent, useCallback, useMemo } from 'react';

import {
  toAbsoluteNumber,
  toAbsolutePoint,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { createAbsolutePoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsolutePoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import ConfigurableCheckboxField from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/ConfigurableCheckboxField';
import {
  AbsolutePoint,
  AbsoluteSize,
  ReactSigningFieldRendererProps,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { withFieldRendererMemo } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';
import SyntheticEventHelper from '@app/libs/syntheticEvent/SyntheticEventHelper';

interface ConfigurableCheckboxFieldRendererProps extends ReactSigningFieldRendererProps {
  maxConstraint: { x: number; y: number };
  onMouseEnter: (e: MouseEvent) => void;
  onMouseLeave: (e: MouseEvent) => void;
  onPositionChangeStart: () => void;
  onPositionChangeEnd: () => void;
  onSizeChangeStart: () => void;
  onSizeChangeEnd: () => void;
}

export type ConfigurableCheckboxFieldComponent =
  React.FunctionComponent<ConfigurableCheckboxFieldRendererProps>;

export const ConfigurableCheckboxFieldRenderer = withFieldRendererMemo(
  ({
    fieldId,
    maxConstraint,
    viewport,
    readOnly,
    restrictModification,
    onPositionChangeStart,
    onPositionChangeEnd,
    onSizeChangeEnd,
    onSizeChangeStart,
    onMouseEnter,
    onMouseLeave,
  }: ConfigurableCheckboxFieldRendererProps) => {
    const { field, setPosition, setSize } = useCheckboxField(fieldId);

    const handleSizeChange = useCallback(
      (absoluteSize: AbsoluteSize, e: React.SyntheticEvent) => {
        const relative = toRelativeSize(
          createAbsoluteSize({ width: absoluteSize.width, height: absoluteSize.width }),
          viewport
        );
        setSize(relative.width, relative.height);
        SyntheticEventHelper.preventTouchMove(e);
      },
      [setSize, viewport]
    );

    const handlePointChange = useCallback(
      (absolutePoint: AbsolutePoint) => {
        const relative = clampFieldPoint(toRelativePoint(absolutePoint, viewport));
        setPosition(relative.x, relative.y);
      },
      [setPosition, viewport]
    );

    const signerOrder =
      field?.participantReference.type === 'SIGNER' ? field?.participantReference.index + 1 : 1;

    const absoluteMaxConstraint: AbsolutePoint = useMemo(() => {
      const maxConstraintX = maxConstraint.x * viewport.width;
      const maxConstraintY = maxConstraint.y * viewport.height;

      const newMaxConstraint = Math.min(maxConstraintY, maxConstraintX);

      // <ResizableBox />에서 lockAspectRatio는 불완전함에 따라
      // maxConstraints를 활용해서 체크박스의 width, height가 1대1 비율을 유지하도록 강제합니다.
      // See: https://github.com/STRML/react-resizable/issues/3
      return createAbsolutePoint({ x: newMaxConstraint, y: newMaxConstraint });
    }, [maxConstraint.x, maxConstraint.y, viewport.height, viewport.width]);

    if (!field || field.type !== 'CHECKBOX') {
      return null;
    }

    return (
      <ConfigurableCheckboxField
        index={field.index}
        maxConstraint={absoluteMaxConstraint}
        point={toAbsolutePoint(field.position, viewport)}
        readOnly={readOnly}
        restrictModification={restrictModification}
        signerOrder={signerOrder}
        size={toAbsoluteNumber(field.size.width, viewport)}
        viewport={viewport}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointChange={handlePointChange}
        onPointChangeStart={onPositionChangeStart}
        onPointChangeStop={onPositionChangeEnd}
        onSizeChange={handleSizeChange}
        onSizeChangeStart={onSizeChangeStart}
        onSizeChangeStop={onSizeChangeEnd}
      />
    );
  }
);
